import { Injectable } from '@angular/core';
import { AppConfig, UvExplorerClient } from 'common';
import { Observable, Subject } from 'rxjs/Rx';
import {
	GetAllUsersResultDto, UserDto, LoginResultDto, LogoutResultDto,
	ActiveDirectorySettingsDto, GoogleWorkspaceSettingsDto,
	GroupDto, GroupTreeDto, GetAuthSettingsResultDto, AccountType,
	GetDirectoryUsersResultDto, GetAllGroupsResultDto,
	GetDirectoryGroupsResultDto,
    GetUsersInGroupResultDto,
    GetUsersInDirectoryGroupResultDto,
    GetDirectoryUsersInDirectoryGroupResultDto,
    GetGroupsContainingUserResultDto,
    GetGroupsContainingDirectoryUserResultDto,
    UserPermissions, SamlSettingsDto
} from 'UvExplorerServer.Dtos.TS/Auth';
import { UpdateResultDto } from 'UvExplorerServer.Dtos.TS';
import { HashSet, StringUtils, ValueEncoder } from "DiscoveryEngine.TS/Utils";
import { List } from "DiscoveryEngine.TS/Utils";
import { SessionStateEntryDto } from '../../UvExplorerServer.Dtos.TS/Session';
import { ObjSerUtils } from '../../DiscoveryEngine.TS/Serialization';


@Injectable()
export class UsersWebService {
	constructor(private appConfig: AppConfig, private uvExplorerClient: UvExplorerClient) {
		return;
	}

	public getAllUsers(): Observable<List<UserDto>> {
		let url = this.appConfig.authServiceUrl + `api/auth/users`;
		let subject = new Subject<List<UserDto>>();

		this.uvExplorerClient.getObjSer<GetAllUsersResultDto>(url, GetAllUsersResultDto).subscribe(
			(result: GetAllUsersResultDto) => {
				subject.next(result.Users);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public createUser(user: UserDto, addedGroups: HashSet<string>, userPassword: string): Observable<UserDto> {

		let body: any = {
			create_user_request: {
				user_password: StringUtils.ObfuscateString2(userPassword),
			}
		};

		user.Write(body.create_user_request, {});

		if (addedGroups.Count > 0) {
			body.create_user_request.added_groups = [];
			ObjSerUtils.WriteStringValueCollection(body.create_user_request.added_groups, addedGroups, {});
		}

		let url = this.appConfig.authServiceUrl + `api/auth/users`;
		let subject = new Subject<UserDto>();

		this.uvExplorerClient.postObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				user.CopyProps(result, true);
				subject.next(user);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateUser(user: UserDto, addedGroups: HashSet<string>, removedGroups: HashSet<string>,
		userPassword: string): Observable<UserDto> {

		let body: any = {
			update_user_request: {
				user_password: StringUtils.ObfuscateString2(userPassword),
			}
		};

		user.Write(body.update_user_request, {});

		if (addedGroups.Count > 0) {
			body.update_user_request.added_groups = [];
			ObjSerUtils.WriteStringValueCollection(body.update_user_request.added_groups, addedGroups, {});
		}

		if (removedGroups.Count > 0) {
			body.update_user_request.removed_groups = [];
			ObjSerUtils.WriteStringValueCollection(body.update_user_request.removed_groups, removedGroups, {});
		}

		let url = this.appConfig.authServiceUrl + `api/auth/users`;
		let subject = new Subject<UserDto>();

		this.uvExplorerClient.putObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				user.CopyProps(result, false);
				subject.next(user);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateUserSettings(user: UserDto): Observable<boolean> {
		let url = this.appConfig.authServiceUrl + `api/auth/update-user-settings`;
		let body: any = {
			user_settings: {
			}
		};

		user.UserSettings.Write(body.user_settings, {});	
		return this.uvExplorerClient.putNoResult(url, body);

	}

	public deleteUser(userGuid: string): Observable<boolean> {
		let body = {
			delete_user_request: {
				user_guid: userGuid
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/users`;
		return this.uvExplorerClient.deleteNoResult(url, body);
	}

	public changePassword(oldUserPassword: string, newUserPassword: string): Observable<boolean> {
		let body = {
			change_password_request: {
				old_user_password: StringUtils.ObfuscateString2(oldUserPassword),
				new_user_password: StringUtils.ObfuscateString2(newUserPassword),
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/users/changepassword`;
		return this.uvExplorerClient.putNoResult(url, body);
	}

	public getAllGroups(): Observable<GroupTreeDto> {
		let url = this.appConfig.authServiceUrl + `api/auth/groups`;
		let subject = new Subject<GroupTreeDto>();

		this.uvExplorerClient.getObjSer<GetAllGroupsResultDto>(url, GetAllGroupsResultDto).subscribe(
			(result: GetAllGroupsResultDto) => {
				subject.next(result.GroupTree);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public createGroup(group: GroupDto, parentGuid: string, position: number): Observable<GroupDto> {

		let body = {
			create_group_request: {
				parent_guid: parentGuid,
				position: ValueEncoder.EncodeInteger(position),
			}
		};

		group.Write(body.create_group_request, {});

		let url = this.appConfig.authServiceUrl + `api/auth/groups`;
		let subject = new Subject<GroupDto>();

		this.uvExplorerClient.postObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				group.CopyProps(result, true);
				subject.next(group);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateGroup(group: GroupDto): Observable<GroupDto> {
		let body = {
			update_group_request: {
			}
		};

		group.Write(body.update_group_request, {});

		let url = this.appConfig.authServiceUrl + `api/auth/groups`;
		let subject = new Subject<GroupDto>();

		this.uvExplorerClient.putObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				group.CopyProps(result, false);
				subject.next(group);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteGroup(groupGuid: string, parentGuid: string): Observable<boolean> {
		let body = {
			delete_group_request: {
				group_guid: groupGuid,
				parent_guid: parentGuid,
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/groups`;
		return this.uvExplorerClient.deleteNoResult(url, body);
	}

	public getGroupsContainingUser(userGuid: string): Observable<GetGroupsContainingUserResultDto> {
		let url = this.appConfig.authServiceUrl + `api/auth/users/groups/${userGuid}`;
		return this.uvExplorerClient.getObjSer(url, GetGroupsContainingUserResultDto);
	}

	public getGroupsContainingDirectoryUser(accountType: AccountType, userDirectoryId: string,
		userDirectoryId_2: string): Observable<GetGroupsContainingDirectoryUserResultDto> {
		let url = this.appConfig.authServiceUrl +
			`api/auth/dirusers/groups/${AccountType[accountType]}/${userDirectoryId}/${userDirectoryId_2}`;
		return this.uvExplorerClient.getObjSer(url, GetGroupsContainingDirectoryUserResultDto);
	}

	public addDirectoryGroupToCache(accountType: AccountType, domainName: string, groupDirectoryId: string,
		groupDirectoryId_2: string): Observable<boolean> {
		let url = this.appConfig.authServiceUrl +
			`api/auth/dirgroups/cache/${AccountType[accountType]}/${domainName}/${groupDirectoryId}/${groupDirectoryId_2}`;
		return this.uvExplorerClient.postNoResult(url, "");
	}

	public getUsersInGroup(groupGuid: string): Observable<GetUsersInGroupResultDto> {
		let url = this.appConfig.authServiceUrl + `api/auth/groups/users/${groupGuid}`;
		return this.uvExplorerClient.getObjSer(url, GetUsersInGroupResultDto);
	}

	public getUsersInDirectoryGroup(accountType: AccountType, domainName: string, groupDirectoryId: string,
		groupDirectoryId_2: string): Observable<GetUsersInDirectoryGroupResultDto> {
		let url = this.appConfig.authServiceUrl +
			`api/auth/dirgroups/users/${AccountType[accountType]}/${domainName}/${groupDirectoryId}/${groupDirectoryId_2}`;
		return this.uvExplorerClient.getObjSer(url, GetUsersInDirectoryGroupResultDto);
	}

	public getDirectoryUsersInDirectoryGroup(accountType: AccountType, domainName: string, groupDirectoryId: string,
		groupDirectoryId_2: string): Observable<GetDirectoryUsersInDirectoryGroupResultDto> {
		let url = this.appConfig.authServiceUrl +
			`api/auth/dirgroups/dirusers/${AccountType[accountType]}/${domainName}/${groupDirectoryId}/${groupDirectoryId_2}`;
		return this.uvExplorerClient.getObjSer(url, GetDirectoryUsersInDirectoryGroupResultDto);
	}

	public loginNativeUser(username: string, password: string): Observable<LoginResultDto> {
		let body = {
			login_request: {
				account_type: AccountType[AccountType.Native],
				user_name: username,
				password: StringUtils.ObfuscateString2(password),
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/login`;
		return this.uvExplorerClient.postObjSer<LoginResultDto>(url, body, LoginResultDto);
	}

	public loginActiveDirectoryUser(username: string, password: string, domain: string): Observable<LoginResultDto> {
		let body = {
			login_request: {
				account_type: AccountType[AccountType.ActiveDirectory],
				user_name: username,
				password: StringUtils.ObfuscateString2(password),
				domain_name: domain,
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/login`;
		return this.uvExplorerClient.postObjSer<LoginResultDto>(url, body, LoginResultDto);
	}

	public loginGoogleWorkspaceUser(finalRedirectUrl: string, errorRedirectUrl: string): Observable<LoginResultDto> {
		let body = {
			login_request: {
				account_type: AccountType[AccountType.GoogleWorkspace],
				final_redirect_url: finalRedirectUrl,
				error_redirect_url: errorRedirectUrl,
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/login`;
		return this.uvExplorerClient.postObjSer<LoginResultDto>(url, body, LoginResultDto);
	}

	public loginSamlUser(finalRedirectUrl: string, errorRedirectUrl: string): Observable<LoginResultDto> {
		let body = {
			login_request: {
				account_type: AccountType[AccountType.Saml],
				final_redirect_url: finalRedirectUrl,
				error_redirect_url: errorRedirectUrl,
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/login`;
		return this.uvExplorerClient.postObjSer<LoginResultDto>(url, body, LoginResultDto);
	}

	public loginActiveDirectoryAdmin(username: string, password: string, domain: string): Observable<LoginResultDto> {
		let body = {
			login_request: {
				account_type: AccountType[AccountType.ActiveDirectory],
				user_name: username,
				password: StringUtils.ObfuscateString2(password),
				domain_name: domain,
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/loginadmin`;
		return this.uvExplorerClient.postObjSer<LoginResultDto>(url, body, LoginResultDto);
	}

	public loginGoogleWorkspaceAdmin(finalRedirectUrl: string, errorRedirectUrl: string): Observable<LoginResultDto> {
		let body = {
			login_request: {
				account_type: AccountType[AccountType.GoogleWorkspace],
				final_redirect_url: finalRedirectUrl,
				error_redirect_url: errorRedirectUrl,
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/loginadmin`;
		return this.uvExplorerClient.postObjSer<LoginResultDto>(url, body, LoginResultDto);
	}

	public loginActiveDirectorySetAuthSettingsAdmin(adSettings: ActiveDirectorySettingsDto,
		username: string, password: string, domain: string): Observable<LoginResultDto> {

		let body = {
			login_set_auth_settings_admin_request: {
				account_type: AccountType[AccountType.ActiveDirectory],
				user_name: username,
				password: StringUtils.ObfuscateString2(password),
				domain_name: domain,
			}
		};
		adSettings.Write(body.login_set_auth_settings_admin_request, {});

		let url = this.appConfig.authServiceUrl + `api/auth/loginsetauthsettingsadmin`;
		return this.uvExplorerClient.postObjSer<LoginResultDto>(url, body, LoginResultDto);
	}

	public loginGoogleWorkspaceSetAuthSettingsAdmin(gwSettings: GoogleWorkspaceSettingsDto, finalRedirectUrl: string,
		errorRedirectUrl: string): Observable<LoginResultDto> {

		let body = {
			login_set_auth_settings_admin_request: {
				account_type: AccountType[AccountType.GoogleWorkspace],
				final_redirect_url: finalRedirectUrl,
				error_redirect_url: errorRedirectUrl,
			}
		};
		gwSettings.Write(body.login_set_auth_settings_admin_request, {});

		let url = this.appConfig.authServiceUrl + `api/auth/loginsetauthsettingsadmin`;
		return this.uvExplorerClient.postObjSer<LoginResultDto>(url, body, LoginResultDto);
	}

	public testAdmin(accountType: AccountType): Observable<LoginResultDto> {
		let url = this.appConfig.authServiceUrl + `api/auth/testadmin/${AccountType[accountType].toLowerCase()}`;
		return this.uvExplorerClient.getObjSer<LoginResultDto>(url, LoginResultDto);
	}

	public getSession(): Observable<LoginResultDto> {
		let url = this.appConfig.authServiceUrl + `api/auth/getsession`;
		return this.uvExplorerClient.getObjSer<LoginResultDto>(url, LoginResultDto);
	}

	public getUserPermissions(): Observable<UserPermissions> {
		let url = this.appConfig.authServiceUrl + `api/auth/getuserpermissions`;
		return this.uvExplorerClient.getObjSer<UserPermissions>(url, UserPermissions);
	}

	public logoutUser(): Observable<LogoutResultDto> {
		let url = this.appConfig.authServiceUrl + `api/auth/logout`;
		return this.uvExplorerClient.postObjSer<LogoutResultDto>(url, "", LogoutResultDto);
	}

	public getAuthSettings(): Observable<GetAuthSettingsResultDto> {
		let url = this.appConfig.authServiceUrl + `api/auth/settings`;
		return this.uvExplorerClient.getObjSer(url, GetAuthSettingsResultDto);
	}

	public setAuthSettings(adSettings: ActiveDirectorySettingsDto,
		gwSettings: GoogleWorkspaceSettingsDto, samlSettings: SamlSettingsDto): Observable<boolean> {

		let body = {
			set_auth_settings_request: {}
		};

		if (adSettings) {
			adSettings.Write(body.set_auth_settings_request, {});
		}
		if (gwSettings) {
			gwSettings.Write(body.set_auth_settings_request, {});
		}
		if (samlSettings) {
			samlSettings.Write(body.set_auth_settings_request, {});
		}

		let url = this.appConfig.authServiceUrl + `api/auth/settings`;
		return this.uvExplorerClient.putNoResult(url, body);
	}

	public getDirectoryUsers(accountType: AccountType, domainName: string, nameFilter: string,
		permanentIdFilter: string, ephemeralIdFilter: string): Observable<GetDirectoryUsersResultDto> {

		let body = {
			get_directory_users_request: {
				account_type: AccountType[accountType],
				domain_name: domainName,
				name_filter: nameFilter,
				ephemeral_id_filter: ephemeralIdFilter,
				permanent_id_filter: permanentIdFilter,
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/directoryusers`;
		return this.uvExplorerClient.postObjSer(url, body, GetDirectoryUsersResultDto);
	}

	public getDirectoryGroups(accountType: AccountType, domainName: string, nameFilter: string,
		permanentIdFilter: string, ephemeralIdFilter: string): Observable<GetDirectoryGroupsResultDto> {

		let body = {
			get_directory_groups_request: {
				account_type: AccountType[accountType],
				domain_name: domainName,
				name_filter: nameFilter,
				ephemeral_id_filter: ephemeralIdFilter,
				permanent_id_filter: permanentIdFilter,
				use_cache: ValueEncoder.EncodeBoolean(false),
				include_member_groups: ValueEncoder.EncodeBoolean(false),
			}
		};

		let url = this.appConfig.authServiceUrl + `api/auth/directorygroups`;
		return this.uvExplorerClient.postObjSer(url, body, GetDirectoryGroupsResultDto);
	}

	public syncDirectoryGroups(accountType: AccountType): Observable<any> {

		let url = this.appConfig.authServiceUrl + `api/auth/directorygroups/sync/${AccountType[accountType].toLowerCase()}`;
		return this.uvExplorerClient.postNoResult(url, "");
	}

	public syncDirectoryGroup(groupGuid: string): Observable<any> {

		let url = this.appConfig.authServiceUrl + `api/auth/directorygroups/sync/${groupGuid}`;
		return this.uvExplorerClient.postNoResult(url, "");
	}

	public setSessionStateEntry(sessionStateEntry: SessionStateEntryDto): Observable<any> {

		let body = {
		};
		sessionStateEntry.Write(body, {});

		let url = this.appConfig.sessionServiceUrl + `api/session/state`;
		return this.uvExplorerClient.putNoResult(url, body);
	}

	public getSessionStateEntry(entryKey: string): Observable<SessionStateEntryDto> {

		let url = this.appConfig.sessionServiceUrl + `api/session/state/${entryKey}`;
		return this.uvExplorerClient.getObjSer(url, SessionStateEntryDto);
	}

	public deleteSessionStateEntry(entryKey: string): Observable<any> {

		let url = this.appConfig.sessionServiceUrl + `api/session/state/${entryKey}`;
		return this.uvExplorerClient.deleteNoResult(url, {});
	}

}
